import { z } from 'zod';

/** Plane Cloud's API. A self-hosted Plane answers the same API at its own origin. */
export const PLANE_CLOUD_API = 'https://api.plane.so';

/**
 * Connecting a Plane workspace (TW-19, TW-22): the workspace, the origin for a self-hosted Plane, and a key, pasted
 * or reused from another connection. A key goes to the server once, is stored encrypted, and is never sent back.
 */
export const PlaneConnectRequest = z.object({
  apiKey: z.string().trim().min(8).max(200).optional(),
  /**
   * Another of the caller's connections whose key to use instead (TW-22). Plane's personal access tokens belong to
   * the person, not the workspace, so a second workspace rarely needs a second key; the server copies it sealed.
   */
  reuseKeyFrom: z.string().uuid().optional(),
  workspaceSlug: z.string().trim().toLowerCase().min(1).max(100).regex(/^[a-z0-9][a-z0-9_-]*$/, 'The part after app.plane.so/ in your workspace URL'),
  baseUrl: z.string().trim().url().optional(),
}).refine((r) => !!r.apiKey !== !!r.reuseKeyFrom, { message: 'Give an API key, or the connection whose key to use' });
export type PlaneConnectRequest = z.infer<typeof PlaneConnectRequest>;

export const PlaneProject = z.object({ id: z.string(), name: z.string(), identifier: z.string() });
export type PlaneProject = z.infer<typeof PlaneProject>;

/** What the client may know about a connection: never the key, only its last four characters. */
export const PlaneConnection = z.object({
  id: z.string(),
  workspaceSlug: z.string(),
  baseUrl: z.string(),
  keyHint: z.string(),
  connectedAt: z.string(),
  lastSyncAt: z.string().nullable(),
  lastError: z.string().nullable(),
});
export type PlaneConnection = z.infer<typeof PlaneConnection>;

export const PlaneConnectResponse = z.object({ connection: PlaneConnection, projects: z.array(PlaneProject) });
export type PlaneConnectResponse = z.infer<typeof PlaneConnectResponse>;

/**
 * Where a Plane project's work items go (TW-19 setup): a Timato project id, `'new'` to create one with the same
 * name, or `null` to leave the project out.
 */
export const PlaneProjectChoice = z.object({ planeProjectId: z.string().min(1), projectId: z.union([z.string().uuid(), z.literal('new'), z.null()]) });
export const PlaneProjectsRequest = z.object({ choices: z.array(PlaneProjectChoice).max(200) });
export type PlaneProjectsRequest = z.infer<typeof PlaneProjectsRequest>;

/** A Plane project and what was decided for it; `decided: false` is one Plane has that setup has not seen yet. */
export const PlaneProjectMapping = PlaneProject.extend({ projectId: z.string().nullable(), decided: z.boolean() });
export type PlaneProjectMapping = z.infer<typeof PlaneProjectMapping>;
export const PlaneProjectsResponse = z.object({ projects: z.array(PlaneProjectMapping) });
export type PlaneProjectsResponse = z.infer<typeof PlaneProjectsResponse>;

/** What one sync did, and the connection after it (its `lastError` says why, when it could not run). */
export const PlaneSyncReport = z.object({ created: z.number().int(), updated: z.number().int(), finished: z.number().int(), reopened: z.number().int(), removed: z.number().int() });
export type PlaneSyncReport = z.infer<typeof PlaneSyncReport>;
export const PlaneSyncResponse = z.object({ report: PlaneSyncReport, connection: PlaneConnection });
export type PlaneSyncResponse = z.infer<typeof PlaneSyncResponse>;

/**
 * Where a Timato project's tasks come from (TW-34): one entry per linked outside project. A project may have
 * several (two Plane projects, two workspaces, later Jira), and one Sync covers them all; only the result is per
 * source. `label` is how the person knows it ("sidewind/TW"); `error` is the connection's last refusal, if any.
 */
export const ProjectSource = z.object({
  provider: z.literal('plane'), connectionId: z.string(), label: z.string(), name: z.string(),
  lastSyncAt: z.string().nullable(), error: z.string().nullable(),
});
export type ProjectSource = z.infer<typeof ProjectSource>;
export const ProjectSourcesResponse = z.object({ sources: z.array(ProjectSource) });
export type ProjectSourcesResponse = z.infer<typeof ProjectSourcesResponse>;
/** A project's sync: the counts across its sources, and the sources after it. */
export const ProjectSyncResponse = z.object({ report: PlaneSyncReport, sources: z.array(ProjectSource) });
export type ProjectSyncResponse = z.infer<typeof ProjectSyncResponse>;
